import { Modal, RowSelectionState, Table, TableColumns } from '@bp/ui-components';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import {
  use_SubjectContainersQuery,
  useImportableLessonsForTeachingBlockVersionQuery,
} from '../../../../types/planung-graphql-client-defs';
import { ModalBottomButtons } from '../../../ModalBottomButtons/ModalBottomButtons';
import { useTeachingBlockForVersion } from '../../../Teachingblock/hooks/useTeachingBlocks';

export type LessonsImportProps = {
  versionUuid: string;
  teachingBlockSubjectContainerUuid: string;
  onClose: () => void;
  onSubmit: (lessonUuids: string[]) => Promise<void>;
  isOpen: boolean;
};

export type ImportLessonsType = {
  uuid: string;
  subject: string;
  classes: string;
  teachers: string;
  elective: boolean;
  teachingLoadEnabled: boolean;
  timetableEnabled: boolean;
};

export const ImportLessonsToTeachingBlockVersionModal = ({
  versionUuid,
  onClose,
  onSubmit,
  teachingBlockSubjectContainerUuid,
  isOpen,
}: LessonsImportProps) => {
  const { t } = useTranslation();

  const [selected, setSelected] = useState({});
  const [sorting, onSorting] = useState([
    { id: 'classes', desc: false },
    { id: 'subject', desc: false },
  ]);

  const [loading, setLoading] = useState(false);

  const schoolYear = useSelectedSchoolYear();

  const lessonContext = useMemorizedCacheTag('LESSONS');

  const { teachingBlock } = useTeachingBlockForVersion(versionUuid);

  const [{ data: subjectContainerResult }] = use_SubjectContainersQuery({});

  const subjectContainerUuid = teachingBlock?.subjectContainerUuid;
  const grades = subjectContainerResult?.subjectContainers.find((sc) => sc.uuid === subjectContainerUuid)?.grades;

  const [{ data: lessonsData }] = useImportableLessonsForTeachingBlockVersionQuery({
    variables: {
      versionUuid,
      subjectContainerUuid: teachingBlockSubjectContainerUuid ?? '',
      schoolYearUuid: schoolYear?.uuid ?? '',
    },
    // additionalTypenames, damit die (ggf. leere) Liste importierbarer Lessons nach dem Anlegen
    // neuer Lessons invalidiert und neu geladen wird.
    context: lessonContext,
    pause: !teachingBlockSubjectContainerUuid || !schoolYear?.uuid,
  });

  const memorizedLessonsData: ImportLessonsType[] = useMemo(() => {
    return (
      lessonsData?.importableLessonsForTeachingBlockVersion.flatMap((lesson): ImportLessonsType[] => {
        const classes = lesson.lessonClasses.map((lessonClass) => lessonClass.class);

        // Nur Zuordnungen, von denen mindestens eine Klasse in einem Jahrgang der Schiene liegt.
        if (!grades || !classes.some((c) => c.grade && grades.includes(c.grade))) {
          return [];
        }
        return [
          {
            subject: lesson.subject.name,
            uuid: lesson.uuid,
            classes: classes.map((c) => c.name).join(', '),
            teachers: lesson.teachers.map((teacher) => teacher.displayNameShort).join(', '),
            elective: lesson.elective ?? false,
            teachingLoadEnabled: lesson.teachingLoadEnabled ?? false,
            timetableEnabled: lesson.timetableEnabled ?? false,
          },
        ];
      }) ?? []
    );
  }, [grades, lessonsData?.importableLessonsForTeachingBlockVersion]);

  function selectedToData<T>(obj: RowSelectionState, arr: T[]) {
    const result = [];
    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        const index = parseInt(key);
        if (index >= 0 && index < arr.length) {
          result.push(arr[index]);
        }
      }
    }
    return result;
  }

  const handleSubmit = async () => {
    setLoading(true);
    const selectedLessons = selectedToData<ImportLessonsType>(selected, memorizedLessonsData);
    const uuids = selectedLessons.map((r) => r.uuid);
    await onSubmit(uuids);

    setLoading(false);
    setSelected({});
    onClose();
  };

  //biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
  const tableColumns = useMemo((): TableColumns<ImportLessonsType>[] => {
    return [
      {
        header: t('lesson.table.subject'),
        id: 'subject',
        accessorKey: 'subject',
        meta: {
          filterName: t('lesson.table.subject'),
        },
        size: 200,
        canExpand: true,
      },
      {
        header: t('lesson.table.classes'),
        id: 'classes',
        accessorKey: 'classes',
        size: 50,
        canExpand: true,
      },
      {
        header: t('lesson.table.teacher'),
        id: 'teachers',
        accessorKey: 'teachers',
        size: 175,
        canExpand: true,
      },
      {
        header: t('lesson.basics.isElective'),
        id: 'elective',
        accessorKey: 'elective',
        type: 'boolean',
        size: 60,
      },
      {
        header: t('lesson.basics.isTeachingLoadEnabled.short'),
        meta: {
          filterName: t('lesson.basics.isTeachingLoadEnabled.full'),
          tooltip: t('lesson.basics.isTeachingLoadEnabled.full'),
        },
        id: 'teachingLoadEnabled',
        accessorKey: 'teachingLoadEnabled',
        type: 'boolean',
        size: 60,
      },
      {
        header: t('lesson.basics.isTimetableEnabled.short'),
        meta: {
          filterName: t('lesson.basics.isTimetableEnabled.full'),
          tooltip: t('lesson.basics.isTimetableEnabled.full'),
        },
        id: 'timetableEnabled',
        accessorKey: 'timetableEnabled',
        type: 'boolean',
        size: 60,
      },
    ];
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => onClose()}
      className={'lesson-import-modal'}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      hideFooter={false}
      height={'l'}
      footer={
        <>
          <ModalBottomButtons
            isLoading={loading}
            closeButton={{ callback: () => onClose() }}
            submitButton={{ callback: () => handleSubmit(), text: t('lesson.import') }}
            errors={undefined}
          />
        </>
      }
    >
      <Table<ImportLessonsType>
        canScroll={false}
        isDesktop={true}
        isOnWhite={false}
        rowSelection={selected}
        onRowSelectionChange={setSelected}
        showActionBar
        actionBarSettings={{
          showExpertFilter: true,
          showAddButton: false,
          showPrintButton: false,
          showBulkEdit: false,
        }}
        sorting={sorting}
        onSortingChange={onSorting}
        columns={tableColumns}
        data={memorizedLessonsData ?? []}
        showSelect
      />
    </Modal>
  );
};
